<template>
  <IvrLayout title="電話自動受付予約（IVR）設定" noIcon :loading="busy">
    <ConfigPage
      :title="type ? '予約受付パターン編集' : '新規予約受付パターン追加'"
      :gap="24"
      scrollable
      showBack
      @back="back"
    >
      <template slot="header">
        <Button @click="back">キャンセル</Button>
        <Button class="is-lead" @click="save" :disabled="!isChanged">保存</Button>
      </template>
      <Form>
        <FormGrid>
          <dt>パターン名</dt>
          <dd>
            <TextInput
              maxlength="100"
              v-model="registData.name"
              placeholder="例）〇/〇～〇/〇のパターン"
              @input="editName()"
              @compositionstart="compositionstart"
              @compositionend="compositionend"
            />
            <p class="note">{{ nameLength }}<span>/100</span></p>
          </dd>
          <dt>設定</dt>
          <dd>
            <RadioList :items="{ 0: '無効', 1: '有効' }" v-model="registData.active" />
          </dd>
          <dt>指定条件</dt>
          <dd>
            <div class="radio-button">
              <RadioButton value="1" v-model.number="inputValue" :disabled="type">
                期間・日付指定
              </RadioButton>
              <RadioButton value="2" v-model.number="inputValue" :disabled="type">
                曜日指定
              </RadioButton>
            </div>
            <div v-if="inputValue === 1">
              <div class="calendar">
                <CalendarInput
                  v-model="registData.startDate"
                  :from="minDate"
                  :to="maxDate"
                  :minDate="minDate"
                  :maxDate="maxDate"
                  :holidayList="allHolidays"
                  :busy="busy"
                  @setDate="setDate()"
                  @beforeShow="setDate(registData.startDate)"
                  @change="changeDate(registData.startDate)"
                />
                〜
                <CalendarInput
                  v-model="registData.endDate"
                  :from="minDate"
                  :to="maxDate"
                  :minDate="minDate"
                  :maxDate="maxDate"
                  :holidayList="allHolidays"
                  :busy="busy"
                  @setDate="setDate()"
                  @beforeShow="setDate(registData.endDate)"
                  @change="changeDate(registData.endDate)"
                />
              </div>
              <p>※最長3ヶ月先までの指定が可能です。</p>
            </div>
            <CheckBoxes v-if="inputValue === 2" :items="weeksItem" v-model="weeksChecked" />
          </dd>
          <dt>時間</dt>
          <IvrTimeInput :data="setTimeFrame" @updateTime="updateTime($event)" />
        </FormGrid>
      </Form>
    </ConfigPage>
  </IvrLayout>
</template>

<script>
import { mapState, mapGetters } from 'vuex'
import { isEqual, cloneDeep } from 'lodash'
import { toMinutes } from '@/utils/time'
import { addMonths, isBefore, isAfter } from 'date-fns'
import { today, toHourClock, relaceToHalf, hasFullWidth } from '@/utils/date'
import { checkModelDependent, replaceKanaFullToHalf, hasHalfKana } from '@/utils/string'
import IvrLayout from '@/components/templates/IvrLayout'
import ConfigPage from '@/components/templates/ConfigPage'
import Button from '@/components/atoms/Button'
import TextInput from '@/components/atoms/TextInput'
import RadioList from '@/components/atoms/RadioList'
import RadioButton from '@/components/atoms/RadioButton'
import CheckBoxes from '@/components/molecules/CheckBoxes'
import Form from '@/components/atoms/Form'
import FormGrid from '@/components/molecules/FormGrid'
import CalendarInput from '@/components/molecules/CalendarInput'
import Dialog from '@/components/molecules/ModalDialog'
import IvrTimeInput from '@/components/organisms/IvrTimeInput'

export default {
  name: 'IvrTime',
  components: {
    IvrLayout,
    ConfigPage,
    Button,
    TextInput,
    RadioList,
    RadioButton,
    CheckBoxes,
    Form,
    FormGrid,
    CalendarInput,
    IvrTimeInput,
  },
  data() {
    return {
      type: this.$route.query.type || null,
      inputValue: 1,
      nameLength: 0,
      setTimeFrame: [{ startTime: null, endTime: null, forwarding: true }],
      weeksChecked: [],
      registData: { active: 1 },
      originData: {},
      composing: false,
    }
  },
  computed: {
    ...mapState({
      ivrTermList: state => state.ivr.list.ivrTermList,
      ivrWeekList: state => state.ivr.list.ivrWeekList,
      busy: state => state.ivr.list.busy,
    }),
    ...mapGetters({
      allHolidays: 'holidays/all',
    }),
    minDate() {
      return today()
    },
    maxDate() {
      return toHourClock(addMonths(new Date(), 3)).date
    },
    weeks() {
      return ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun', 'holiday']
    },
    weeksItem() {
      return new Map([
        ['mon', '月'],
        ['tue', '火'],
        ['wed', '水'],
        ['thu', '木'],
        ['fri', '金'],
        ['sat', '土'],
        ['sun', '日'],
        ['holiday', '祝日'],
      ])
    },
    isChanged() {
      const typeCheck =
        this.inputValue === 1 ? this.registData.startDate && this.registData.endDate : this.weeksChecked.length
      // 時間が未設定のものは除外
      const timeLengthCheck = this.setTimeFrame.filter(item => item.startTime !== null && item.endTime !== null).length

      return this.type
        ? this.nameLength && timeLengthCheck && typeCheck && !isEqual(this.originData, this.registData)
        : this.nameLength && timeLengthCheck && typeCheck
    },
  },
  watch: {
    // 新規登録時のみ指定条件ラジオボタン押下後は入力内容をリセット
    inputValue(val) {
      if (val === 1) {
        this.weeksChecked = []
        return
      }
      delete this.registData.startDate
      delete this.registData.endDate
    },
    weeksChecked() {
      // 曜日をセット
      this.weeks.map(week => (this.registData[week] = this.weeksChecked.filter(item => item === week).length > 0))
    },
    isChanged() {
      this.$store.commit('app/editing', this.isChanged)
    },
  },
  async created() {
    await this.$store.dispatch('holidays/get')
    if (this.$route.query.id) await this.fetch(true)
  },
  methods: {
    async fetch(readonly) {
      const { dispatch } = this.$store
      // 指定日別パターン編集の場合
      if (this.type === 'term') {
        await dispatch('ivr/list/getTerms', { readonly })
        this.registData = this.ivrTermList.terms.filter(v => v.id === Number(this.$route.query.id))[0]
      }
      // 曜日別パターン編集の場合
      if (this.type === 'week') {
        await dispatch('ivr/list/getWeeks', { readonly })
        this.inputValue = 2
        this.registData = this.ivrWeekList.weeks.filter(v => v.id === Number(this.$route.query.id))[0]
        // 曜日のチェックボックスにセット
        this.weeksChecked = this.weeks.map(week => this.registData[week] && week).filter(v => v)
      }
      this.registData.active = this.registData.active ? 1 : 0
      this.nameLength = this.registData.name.length
      this.setTimeFrame = this.registData.timeFrame
      this.originData = cloneDeep(this.registData)
    },
    async setDate(date) {
      await this.$store.dispatch('init/createCalendar', { date: date || today() })
    },
    changeDate(date) {
      if (isAfter(date, this.registData.endDate)) this.registData.endDate = date
      if (isBefore(date, this.registData.startDate)) this.registData.startDate = date
    },
    editName() {
      this.nameLength = this.registData.name.length
      if (!this.registData.name) return

      if (!this.composing) {
        // 全角英数記号を半角に変換
        if (hasFullWidth(this.registData.name)) {
          this.registData.name = relaceToHalf(this.registData.name)
        }
        // 半角カナを全角に変換
        if (hasHalfKana(this.registData.name)) {
          this.registData.name = replaceKanaFullToHalf(this.registData.name)
        }
      }
    },
    compositionstart() {
      this.composing = true
    },
    compositionend() {
      this.composing = false
      this.editName()
    },
    updateTime(value) {
      // 時間が未設定のものは登録用データから除外
      this.registData.timeFrame = value.filter(item => item.startTime !== null && item.endTime !== null)
      this.setTimeFrame = value
    },
    // 設定した受付時間の重複チェック
    isTimeOverlap(list) {
      for (let i = 0; i < list.length; i++) {
        const a = list[i]
        if (a.startTime === a.endTime) return true
        // パターンが１つだけの場合はreturn
        if (list.length < 2) return false

        for (let j = 1; j < list.length; j++) {
          const b = list[j]
          if (i === j) break
          if (a.startTime === b.startTime || a.endTime === b.endTime) return true
          const startA = toMinutes(a.startTime)
          const startB = toMinutes(b.startTime)
          const endA = toMinutes(a.endTime)
          const endB = toMinutes(b.endTime)
          if (startB < endA && startA < endB) return true
        }
      }
      return false
    },
    async save() {
      if (checkModelDependent(this.registData.name)) {
        await Dialog.show({
          title: '予約受付パターン',
          message: 'パターン名に機種依存文字が使用されています。\n入力内容を確認してください。',
          okText: '閉じる',
        })
        return
      }
      if (this.isTimeOverlap(this.registData.timeFrame)) {
        await Dialog.show({
          title: '予約受付パターン',
          message: '設定した時間が重複しています。\nひとつの受付パターン内で設定時間の重複はできません。',
          okText: '閉じる',
        })
        return
      }

      // 表示順は新規登録時に-1、編集時は0を固定で登録
      this.registData.displayOrder = this.type ? 0 : -1
      this.registData.active = this.registData.active === 1
      if (this.inputValue === 1) {
        if (await this.$store.dispatch('ivr/edit/saveTerms', { terms: [this.registData] })) this.back()
      } else if (await this.$store.dispatch('ivr/edit/saveWeeks', { weeks: [this.registData] })) this.back()
    },
    back() {
      this.$store.commit('app/editing', false)
      this.$router.go(-1)
    },
  },
}
</script>

<style lang="postcss" scoped>
.note {
  font-size: 13px;
  margin-bottom: -12px;
  text-align: right;

  span {
    font-size: 11px;
  }
}
.radio-button {
  grid-template-columns: max-content 1fr;

  label + label {
    margin-left: 24px;
  }
}

.calendar {
  align-items: center;
  display: grid;
  grid-auto-flow: column;
  grid-gap: 8px;
  justify-content: flex-start;
  padding-bottom: 8px;

  >>> &-button {
    fill: var(--very-dark-gray-2);
  }
}
.check-boxes {
  grid-auto-flow: unset;
  grid-template-columns: 86px 86px 86px 86px 86px;
}
</style>
